// Product Development - Simplified ProductDetails component
// Only shows fields from screenshot - no tables
import React, { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  Button,
  Box,
  Checkbox,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";
import { useLocation } from "react-router-dom";

// Import common styles
import { inputFieldStyles, commonInlineStyles } from "../../../styles";
import { appUrl } from "../../../../../../../../utils/config";

const useStyles = makeStyles((theme) => ({
  formField: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
    },
  },
  select: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
}));

const ProductDetails = ({
  formData,
  updateStepData,
  classes: parentClasses,
  readOnly = false,
  handleNext,
  handleBack,
  handleSubmit,
  activeStep,
  stepsLength,
  onValidationChange = () => {},
  markApiCallSuccessful,
}) => {
  const location = useLocation();
  const classes = useStyles();
  
  const selectedProductType = location.state?.selectedSupplytype || {};
  const selectedProductDetails = location.state?.productDetails || {};
  const selectedProduct = location.state?.selectedProduct || {};

  // Initialize step data - map CMO fields from cmoProductDetailsList if direct fields are missing
  const [stepData, setStepData] = useState(() => {
    if (formData?.productDetails) {
      const productDetails = formData.productDetails;
      return {
        ...productDetails,
        supplyTypeID: productDetails.supplyTypeID ?? selectedProductType?.supplyTypeID ?? "",
        cmoPlantId: productDetails?.vendorMosymphonyID && productDetails?.vendorName 
          ? `${productDetails.vendorMosymphonyID}|${productDetails.vendorName}|${productDetails.vendorPlant || ""}`
          : "",
        // Map CMO fields from cmoProductDetailsList[0] if direct fields are missing
        cmoProductCode: productDetails.cmoProductCode || 
                       productDetails.cmoProductDetailsList?.[0]?.cmoProductCode || 
                       "",
        cmoProductDescription: productDetails.cmoProductDescription || 
                              productDetails.cmoProductDetailsList?.[0]?.cmoProductDescription || 
                              "",
        cmoUOM: productDetails.cmoUOM || 
                productDetails.cmoProductDetailsList?.[0]?.cmoUOM || 
                "",
        // Ensure cmoProductDetailsList is always an array
        cmoProductDetailsList: productDetails.cmoProductDetailsList || [],
        // Map qaCheckRequired from processFlowConfiguration (like commercial supply)
        qaCheckRequired: formData?.processFlowConfiguration?.sponsorCQAApprovalReq ?? 
                        formData?.processFlowConfiguration?.qaCheckRequired ?? 
                        productDetails?.qaCheckRequired ?? false,
      };
    } else {
      return {
        productCode: selectedProductDetails?.productCode ?? "",
        productDescription: selectedProductDetails?.productDescription ?? "",
        brand: selectedProductDetails?.brand ?? "",
        status: selectedProductDetails?.status ?? "",
        supplyTypeID: selectedProductType?.supplyTypeID ?? "",
        productTypeID: selectedProductDetails?.productTypeID ?? "",
        facilityTypeID: selectedProductDetails?.facilityTypeID ?? "",
        drugTypeID: selectedProductDetails?.drugTypeID ?? "",
        vendorName: selectedProductDetails?.vendorName ?? "",
        vendorMosymphonyID: selectedProductDetails?.vendorMosymphonyID ?? "",
        productModality: selectedProductDetails?.drugTypeName ?? "",
        productTypeName: selectedProductDetails?.productTypeName ?? "",
        manufacturingFacilityType: selectedProductDetails?.facilityTypeName ?? "",
        receivingStorageLocationIDsFromSponsorERP: (() => {
          const value = selectedProductDetails?.receivingStorageLocationIDsFromSponsorERP;
          if (Array.isArray(value)) return value;
          if (typeof value === "string" && value)
            return value.split(",").map((item) => item.trim()).filter((item) => item !== "");
          return [];
        })(),
        receivingPlantIDFromSponsorERP: selectedProductDetails?.receivingPlantIDFromSponsorERP ?? "",
        sponsorUOM: selectedProductDetails?.sponsorUOM ?? "",
        cmoUOM: selectedProductDetails?.cmoUOM ?? "",
        cmoPlantId: (selectedProductDetails?.cmoPlantId ??
          `${selectedProductDetails?.vendorMosymphonyID}|${selectedProductDetails?.vendorName}|${selectedProductDetails?.vendorPlant || ""}`) || "",
        cmoProductCode: selectedProductDetails?.cmoProductCode ?? "",
        cmoProductDescription: selectedProductDetails?.cmoProductDescription ?? "",
        manufactureSupplyType: selectedProductDetails?.manufactureSupplyType ?? "",
        // Map qaCheckRequired from processFlowConfiguration if available, otherwise from selectedProductDetails
        qaCheckRequired: selectedProductDetails?.qaCheckRequired ?? false,
        pendingConfiguration: true,
        collabrixProductID: selectedProductDetails?.collabrixProductID ?? "",
        // Ensure cmoProductDetailsList is always an array
        cmoProductDetailsList: selectedProductDetails?.cmoProductDetailsList || [],
      };
    }
  });

  const [errors, setErrors] = useState({});
  const [hasValidationAttempted, setHasValidationAttempted] = useState(false);
  
  // Brand dialog states
  const [openBrandDialog, setOpenBrandDialog] = useState(false);
  const [newBrand, setNewBrand] = useState("");
  const [brandError, setBrandError] = useState("");
  
  // Loading states
  const [isLoading, setIsLoading] = useState({
    productModalities: true,
    productTypes: true,
    facilityTypes: true,
    brands: true,
    vendorPlants: true,
  });

  // Data states
  const [productModalities, setProductModalities] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [facilityTypes, setFacilityTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [vendorPlants, setVendorPlants] = useState([]);
  const [plantIDStorageID, setPlantIDStorageID] = useState({});

  // API base URL
  const apiBaseUrl = useMemo(() => {
    const appName = localStorage.getItem("appName");
    const network = localStorage.getItem("network");
    const collabType = localStorage.getItem("collabType");
    const roleName = localStorage.getItem("roleName");
    if (appName && network && collabType && roleName) {
      return `${appUrl}/${appName}/${network}/${collabType}/${roleName}`;
    }
    return null;
  }, []);

  // Fetch dropdown data
  useEffect(() => {
    const fetchData = async () => {
      if (!apiBaseUrl) return;

      // Helper function to handle API errors
      const handleApiError = (key, message) => {
        console.error(message);
        setIsLoading(prev => ({ ...prev, [key]: false }));
      };

      // Fetch Product Modalities
      const fetchProductModalities = async () => {
        try {
          const modalitiesRes = await axios.get(`${apiBaseUrl}/fetchPlantDrugTypes/true`);
          if (modalitiesRes.data?.data?.drugTypeList) {
            setProductModalities(modalitiesRes.data.data.drugTypeList);
            setIsLoading(prev => ({ ...prev, productModalities: false }));
            
            // If we have existing productModality data, find and set the corresponding ID
            if (
              stepData?.productModality &&
              !stepData?.drugTypeID &&
              modalitiesRes.data.data.drugTypeList
            ) {
              const matchingModality = modalitiesRes.data.data.drugTypeList.find(
                (modality) => modality.drugTypeName === stepData.productModality
              );
              if (matchingModality) {
                const updatedStepData = {
                  ...stepData,
                  drugTypeID: matchingModality.drugTypeID,
                };
                setStepData(updatedStepData);
                updateStepData("productDetails", updatedStepData);
              }
            }
          } else {
            setIsLoading(prev => ({ ...prev, productModalities: false }));
          }
        } catch (error) {
          handleApiError("productModalities", "Error fetching product modalities:", error);
        }
      };

      // Fetch Product Types
      const fetchProductTypes = async () => {
        try {
          const response = await axios.get(`${apiBaseUrl}/fetchProductTypes/true`);
          setProductTypes(response.data.data.productTypeList);
          setIsLoading(prev => ({ ...prev, productTypes: false }));
        } catch (error) {
          handleApiError("productTypes", "Failed to load product types");
        }
      };

      // Fetch Facility Types
      const fetchFacilityTypes = async () => {
        try {
          const facilityTypesRes = await axios.get(`${apiBaseUrl}/fetchFacilityTypes/true`);
          if (facilityTypesRes.data?.data?.facilityTypesList) {
            setFacilityTypes(facilityTypesRes.data.data.facilityTypesList);
            
            // If we have existing manufacturingFacilityType data, find and set the corresponding ID
            if (
              stepData?.manufacturingFacilityType &&
              !stepData?.facilityTypeID &&
              facilityTypesRes.data.data.facilityTypesList
            ) {
              const matchingFacilityType = facilityTypesRes.data.data.facilityTypesList.find(
                (facilityType) =>
                  facilityType.facilityTypeName === stepData.manufacturingFacilityType
              );
              if (matchingFacilityType) {
                const updatedStepData = {
                  ...stepData,
                  facilityTypeID: matchingFacilityType.facilityTypeID,
                };
                setStepData(updatedStepData);
                updateStepData("productDetails", updatedStepData);
              }
            }
          }
          setIsLoading(prev => ({ ...prev, facilityTypes: false }));
        } catch (error) {
          handleApiError("facilityTypes", "Error fetching facility types:", error);
        }
      };

      // Fetch Brands
      const fetchBrands = async () => {
        try {
          const response = await axios.get(`${apiBaseUrl}/fetchAllBrands`);
          // Handle different response structures - matching Commercial Supply
          const brandsData = response.data?.data?.Brands || response.data?.Brands || [];
          if (Array.isArray(brandsData)) {
            setBrands(brandsData);
          } else {
            setBrands([]);
          }
          setIsLoading((prev) => ({ ...prev, brands: false }));
        } catch (error) {
          handleApiError("brands", "Failed to load brands");
        }
      };

      // Fetch all data in parallel
      await Promise.all([
        fetchProductModalities(),
        fetchProductTypes(),
        fetchFacilityTypes(),
        fetchBrands(),
      ]);
    };

    fetchData();
  }, [apiBaseUrl]);

  // Function to fetch vendor plants data - matching Commercial Supply implementation
  const fetchVendorPlantsData = async (data = stepData) => {
    // Check if required IDs are available before making the API call
    if (!apiBaseUrl || !data?.drugTypeID || !data?.facilityTypeID) {
      return; // Skip API call if required fields are missing
    }

    try {
      setIsLoading(prev => ({ ...prev, vendorPlants: true }));
      const payload = {
        drugTypeID: data.drugTypeID,
        supplyTypeID: data.supplyTypeID || selectedProductType?.supplyTypeID,
        facilityTypeID: data.facilityTypeID,
      };
      // Fetch vendor plants from the backend using POST
      const response = await axios.post(
        `${apiBaseUrl}/fetchVendorPlants`,
        payload
      );

      // Ensure the selected plant (from product details) is present in the dropdown
      if (
        data.vendorMosymphonyID &&
        response.data.vendorPlantDetails &&
        !response.data.vendorPlantDetails.some(
          (p) => String(p.vendorMosymphonyID) === String(data.vendorMosymphonyID)
        )
      ) {
        response.data.vendorPlantDetails.push({
          vendorPlant: data.vendorPlant || "",
          vendorName: data.vendorName,
          vendorMosymphonyID: Number(data.vendorMosymphonyID),
        });
      }

      // Normalize all vendorPlantDetails to ensure vendorPlant is always a name/code, never an ID
      const normalizedVendorPlants = response.data.vendorPlantDetails
        .map((plant) => ({
          ...plant,
          vendorPlant:
            typeof plant.vendorPlant === "number"
              ? String(plant.vendorPlant)
              : plant.vendorPlant || "",
        }))
        .filter((plant) => plant?.vendorMosymphonyID); // Only keep plants with a valid ID
      
      setVendorPlants(normalizedVendorPlants);
      setIsLoading(prev => ({ ...prev, vendorPlants: false }));
    } catch (error) {
      console.error("Error fetching vendor plants:", error);
      setIsLoading(prev => ({ ...prev, vendorPlants: false }));
    }
  };

  // Fetch vendor plants when drugTypeID and facilityTypeID are available
  useEffect(() => {
    if (stepData.drugTypeID && stepData.facilityTypeID) {
      fetchVendorPlantsData();
    }
  }, [apiBaseUrl, stepData.drugTypeID, stepData.facilityTypeID, stepData.supplyTypeID]);

  // Fetch receiving plant IDs and storage location IDs when CMO is selected
  useEffect(() => {
    const vendorMosymphonyID =
      formData?.productDetails?.vendorMosymphonyID ||
      stepData?.vendorMosymphonyID;

    // Only call API if vendorMosymphonyID is available
    if (!vendorMosymphonyID || !apiBaseUrl) {
      setPlantIDStorageID({});
      return;
    }

    const fetchPlantIDStorageID = async () => {
      try {
        const response = await axios.get(
          `${apiBaseUrl}/fetchAllReceivingPlantIDsAndStorageLocationIDs/${vendorMosymphonyID}`
        );
        setPlantIDStorageID(response.data);
      } catch (error) {
        console.error("Error fetching plant ID and storage location IDs:", error);
      }
    };

    fetchPlantIDStorageID();
  }, [
    formData?.productDetails?.vendorMosymphonyID,
    stepData?.vendorMosymphonyID,
    apiBaseUrl,
  ]);

  // Extract receiving plant IDs and storage location IDs from the API response
  const receivingStorageLocationIDs =
    plantIDStorageID?.vendorOrgLevelData?.[0]?.receivingStorageLocationIDs || [];

  const receivingPlantIDs =
    plantIDStorageID?.vendorOrgLevelData?.[0]?.receivingPlantIDs || [];

  // Handle field changes
  const handleChange = (event) => {
    const { name, value } = event.target;
    
    // Special handling for boolean fields like qaCheckRequired
    let safeValue;
    if (name === "qaCheckRequired") {
      // Handle boolean conversion for qaCheckRequired dropdown
      if (value === null || value === "null" || value === "") {
        safeValue = false; // Default to false if null or empty
      } else if (value === true || value === "true") {
        safeValue = true;
      } else if (value === false || value === "false") {
        safeValue = false;
      } else {
        safeValue = Boolean(value);
      }
    } else {
      safeValue = value !== undefined && value !== null ? value : "";
    }

    const updatedStepData = { ...stepData, [name]: safeValue };

    // Special handling for composite fields
    if (name === "cmoPlantId" && value) {
      const [vendorMosymphonyID, vendorName, vendorPlant] = value.split("|");
      const selectedVendor = vendorPlants.find(
        (plant) =>
          String(plant?.vendorMosymphonyID) === vendorMosymphonyID &&
          plant.vendorPlant === vendorPlant
      );
      updatedStepData.vendorMosymphonyID = Number(vendorMosymphonyID);
      updatedStepData.vendorPlant = vendorPlant;
      updatedStepData.vendorName = selectedVendor
        ? selectedVendor.vendorName
        : vendorName;
      
      // Trigger fetch of receiving plant IDs and storage location IDs when CMO changes
      if (vendorMosymphonyID) {
        // This will trigger the useEffect that fetches plant IDs
      }
    }

    if (name === "productModality" && safeValue) {
      const selectedModality = productModalities?.find(m => m.drugTypeName === safeValue);
      updatedStepData.drugTypeID = selectedModality ? selectedModality.drugTypeID : "";
      // Trigger vendor plants fetch when drugTypeID changes
      if (selectedModality && updatedStepData.facilityTypeID) {
        setTimeout(() => fetchVendorPlantsData(updatedStepData), 0);
      }
    }

    if (name === "productTypeName" && safeValue) {
      const selectedProductType = productTypes?.find(p => p.productTypeName === safeValue);
      updatedStepData.productTypeID = selectedProductType ? selectedProductType.productTypeID : "";
    }

    if (name === "manufacturingFacilityType" && safeValue) {
      const selectedFacilityType = facilityTypes?.find(f => f.facilityTypeName === safeValue);
      updatedStepData.facilityTypeID = selectedFacilityType ? selectedFacilityType.facilityTypeID : "";
      // Trigger vendor plants fetch when facilityTypeID changes
      if (selectedFacilityType && updatedStepData.drugTypeID) {
        setTimeout(() => fetchVendorPlantsData(updatedStepData), 0);
      }
    }

    // Handle multi-select for receivingStorageLocationIDsFromSponsorERP
    if (name === "receivingStorageLocationIDsFromSponsorERP") {
      updatedStepData[name] = typeof value === "string" ? value.split(",") : value;
    }

    // Handle CMO fields - sync with cmoProductDetailsList array
    if (name === "cmoProductCode" || name === "cmoProductDescription" || name === "cmoUOM") {
      // Ensure cmoProductDetailsList exists and has at least one item
      let cmoProductDetailsList = updatedStepData.cmoProductDetailsList || [];
      if (cmoProductDetailsList.length === 0) {
        cmoProductDetailsList = [{ cmoProductCode: "", cmoProductDescription: "", cmoUOM: "" }];
      }
      
      // Update the first item in the array with the new value
      cmoProductDetailsList[0] = {
        ...cmoProductDetailsList[0],
        [name === "cmoProductCode" ? "cmoProductCode" : 
         name === "cmoProductDescription" ? "cmoProductDescription" : 
         "cmoUOM"]: safeValue
      };
      
      updatedStepData.cmoProductDetailsList = cmoProductDetailsList;
    }

    setStepData(updatedStepData);
    updateStepData("productDetails", updatedStepData);

    // OnChange validation for fields - matching Commercial Supply
    const newErrors = { ...errors };
    
    // Validation regex - matching Commercial Supply
    const productCodeRegex = /^[A-Za-z0-9\-_]+$/;
    const productDescriptionRegex = /^[A-Za-z0-9\-_.,() ]+$/;
    const cmoProductCodeRegex = /^[A-Za-z0-9\-_/]+$/;
    const cmoProductDescriptionRegex = /^[A-Za-z0-9\-_.,()&/ ]+$/;

    if (name === "productCode") {
      if (!safeValue) {
        newErrors[name] = "Product Code is required";
      } else if (!productCodeRegex.test(safeValue)) {
        newErrors[name] =
          "Product Code can only contain alphabets, numbers, hyphens (-), and underscores (_)";
      } else {
        delete newErrors[name];
      }
    }

    if (name === "productDescription") {
      if (!safeValue) {
        newErrors[name] = "Product Description is required";
      } else if (!productDescriptionRegex.test(safeValue)) {
        newErrors[name] =
          "Product Description can only contain alphabets, numbers, spaces, hyphens (-), underscores (_), periods (.), commas (,), and parentheses ()";
      } else {
        delete newErrors[name];
      }
    }

    if (name === "cmoProductCode") {
      if (!safeValue) {
        newErrors[name] = "CMO Product Code is required";
        newErrors["cmoProductCode-0"] = "CMO Product Code is required";
      } else if (!cmoProductCodeRegex.test(safeValue)) {
        newErrors[name] =
          "CMO Product Code can only contain alphabets, numbers, hyphens (-), underscores (_), and slashes (/)";
        newErrors["cmoProductCode-0"] =
          "CMO Product Code can only contain alphabets, numbers, hyphens (-), underscores (_), and slashes (/)";
      } else {
        delete newErrors[name];
        delete newErrors["cmoProductCode-0"];
      }
    }

    if (name === "cmoProductDescription") {
      if (!safeValue) {
        newErrors[name] = "CMO Product Description is required";
        newErrors["cmoProductDescription-0"] = "CMO Product Description is required";
      } else if (!cmoProductDescriptionRegex.test(safeValue)) {
        newErrors[name] =
          "CMO Product Description can only contain alphabets, numbers, spaces, hyphens (-), underscores (_), periods (.), commas (,), parentheses (), ampersands (&), and slashes (/)";
        newErrors["cmoProductDescription-0"] =
          "CMO Product Description can only contain alphabets, numbers, spaces, hyphens (-), underscores (_), periods (.), commas (,), parentheses (), ampersands (&), and slashes (/)";
      } else {
        delete newErrors[name];
        delete newErrors["cmoProductDescription-0"];
      }
    }

    if (name === "cmoUOM") {
      if (!safeValue) {
        newErrors[name] = "CMO UOM is required";
        newErrors["cmoUOM-0"] = "CMO UOM is required";
      } else {
        delete newErrors[name];
        delete newErrors["cmoUOM-0"];
      }
    }

    // Clear error for other fields
    if (errors[name] && !newErrors[name]) {
      delete newErrors[name];
    }

    setErrors(newErrors);
  };

  // Validation - matching Commercial Supply
  const validateForm = () => {
    setHasValidationAttempted(true);
    // Field-to-label mapping for better error messages in UI
    const fieldLabels = {
      productCode: "Product Code",
      productDescription: "Product Description",
      productModality: "Product Modality",
      productTypeName: "Product Type",
      manufacturingFacilityType: "Manufacturing Facility Type",
      receivingPlantIDFromSponsorERP: "Receiving Plant ID From Sponsor ERP",
      sponsorUOM: "Sponsor UOM",
      brand: "Brand",
      cmoPlantId: "CMO Name",
      status: "Product Status",
    };

    // List of fields that are required to be filled before proceeding
    const mandatoryFields = [
      "productCode",
      "productDescription",
      "productModality",
      "productTypeName",
      "manufacturingFacilityType",
      "brand",
      "cmoPlantId",
      "status",
      "receivingPlantIDFromSponsorERP",
      "sponsorUOM",
    ];

    const newErrors = {};
    let isValid = true;

    // Validation regex - matching Commercial Supply
    const productCodeRegex = /^[A-Za-z0-9\-_]+$/; // Alphabets, Numbers (0-9), Hyphens (-), Underscore (_)
    const productDescriptionRegex = /^[A-Za-z0-9\-_.,() ]+$/; // Alphabets, Spaces, Numbers (0-9), Hyphen(-), Point(.), Comma(,), parentheses()
    const cmoProductCodeRegex = /^[A-Za-z0-9\-_/]+$/; // Alphabets, Numbers (0-9), Hyphens (-), Underscore (_), slash (/)
    const cmoProductDescriptionRegex = /^[A-Za-z0-9\-_.,()&/ ]+$/; // Alphabets, Numbers (0-9), Hyphens (-), Underscore (_), slash (/), Ampersand (&), Parentheses(), Period (.)

    // Check each mandatory field for empty values
    mandatoryFields.forEach((field) => {
      // Skip validation if field is in read-only mode
      if (!shouldShowEditableField(field, readOnly, selectedProduct)) {
        return;
      }

      const value = stepData[field];
      
      // Special handling for boolean fields
      // For other fields, use the original validation logic
      const isEmpty =
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "") ||
        (Array.isArray(value) && value.length === 0);

      if (isEmpty) {
        newErrors[field] = `${fieldLabels[field]} is required`;
        isValid = false;
      } else {
        // Custom validation for Product Code
        if (
          field === "productCode" &&
          !productCodeRegex.test(stepData[field])
        ) {
          newErrors[field] =
            "Product Code can only contain alphabets, numbers, hyphens (-), and underscores (_)";
          isValid = false;
        }
        // Custom validation for Product Description
        if (
          field === "productDescription" &&
          !productDescriptionRegex.test(stepData[field])
        ) {
          newErrors[field] =
            "Product Description can only contain alphabets, numbers, spaces, hyphens (-), underscores (_), periods (.), commas (,), and parentheses ()";
          isValid = false;
        }
      }
    });

    // Validate CMO Product Details List (array format) - matching Commercial Supply
    const cmoProductDetailsList = stepData.cmoProductDetailsList || [];
    const cmoErrors = {};
    
    if (cmoProductDetailsList.length === 0) {
      cmoErrors["cmoProductDetailsList"] = "At least one CMO Product Detail is required";
      isValid = false;
    } else {
      cmoProductDetailsList.forEach((row, index) => {
        if (!row.cmoProductCode || row.cmoProductCode.trim() === "") {
          cmoErrors[`cmoProductCode-${index}`] = "CMO Product Code is required";
          isValid = false;
        } else if (!cmoProductCodeRegex.test(row.cmoProductCode)) {
          cmoErrors[`cmoProductCode-${index}`] = "CMO Product Code can only contain alphabets, numbers, hyphens (-), underscores (_), and slashes (/)";
          isValid = false;
        }
        
        if (!row.cmoProductDescription || row.cmoProductDescription.trim() === "") {
          cmoErrors[`cmoProductDescription-${index}`] = "CMO Product Description is required";
          isValid = false;
        } else if (!cmoProductDescriptionRegex.test(row.cmoProductDescription)) {
          cmoErrors[`cmoProductDescription-${index}`] = "CMO Product Description can only contain alphabets, numbers, spaces, hyphens (-), underscores (_), periods (.), commas (,), parentheses (), ampersands (&), and slashes (/)";
          isValid = false;
        }
        
        if (!row.cmoUOM || row.cmoUOM.trim() === "") {
          cmoErrors[`cmoUOM-${index}`] = "CMO UOM is required";
          isValid = false;
        }
      });
    }

    // Merge CMO errors into main errors object
    Object.keys(cmoErrors).forEach(key => {
      newErrors[key] = cmoErrors[key];
    });

    setErrors(newErrors);
    onValidationChange(isValid);
    return isValid;
  };

  // Handle next with validation and save API call - matching Commercial Supply
  const handleNextWithValidation = async () => {
    if (!validateForm()) {
      return;
    }

    // Set loading state
    setIsLoading(prev => ({ ...prev, savingData: true }));

    try {
      if (!apiBaseUrl) {
        console.error("API base URL not available");
        setIsLoading(prev => ({ ...prev, savingData: false }));
        return;
      }

      // Check if product already exists (has collabrixProductID)
      const hasExistingProduct = formData?.productDetails?.collabrixProductID || stepData?.collabrixProductID;

      // Construct cmoProductDetailsList from direct fields if not already present
      let cmoProductDetailsList = stepData.cmoProductDetailsList || [];
      
      // If cmoProductDetailsList is empty or doesn't have valid data, construct it from direct fields
      if (!cmoProductDetailsList || cmoProductDetailsList.length === 0 || 
          !cmoProductDetailsList[0]?.cmoProductCode) {
        // Check if we have any CMO field values to create the list
        if (stepData.cmoProductCode || stepData.cmoProductDescription || stepData.cmoUOM) {
          cmoProductDetailsList = [{
            cmoProductCode: stepData.cmoProductCode || "",
            cmoProductDescription: stepData.cmoProductDescription || "",
            cmoUOM: stepData.cmoUOM || "",
          }];
        } else {
          cmoProductDetailsList = [];
        }
      }

      // Prepare payload - exclude direct CMO fields and use cmoProductDetailsList instead
      // Also exclude qaCheckRequired from productDetails as it goes to processFlowConfiguration
      const { cmoProductCode, cmoProductDescription, cmoUOM, qaCheckRequired, ...stepDataWithoutCMOFields } = stepData;
      
      const payload = {
        productDetails: {
          ...stepDataWithoutCMOFields,
          drugTypeID: stepData.drugTypeID,
          drugTypeName: stepData.productModality,
          pendingConfiguration: true,
          // Format receivingStorageLocationIDsFromSponsorERP as comma-separated string
          receivingStorageLocationIDsFromSponsorERP: Array.isArray(stepData.receivingStorageLocationIDsFromSponsorERP)
            ? stepData.receivingStorageLocationIDsFromSponsorERP.join(",")
            : stepData.receivingStorageLocationIDsFromSponsorERP || "",
          // Use cmoProductDetailsList array format instead of direct fields
          cmoProductDetailsList: cmoProductDetailsList,
        },
        // Map qaCheckRequired to processFlowConfiguration (like commercial supply)
        updateProcessFlowConfigFlag: true,
        processFlowConfiguration: {
          sponsorCQAApprovalReq: stepData.qaCheckRequired === true ? true : false, // true if selected YES, false if NO
        },
      };

      let response;
      if (hasExistingProduct) {
        // Update existing product
        payload.updateProductDetailsFlag = true;
        payload.productDetails.collabrixProductID = hasExistingProduct;
        response = await axios.post(`${apiBaseUrl}/updateProductDetails`, payload);
      } else {
        // Create new product
        response = await axios.post(`${apiBaseUrl}/addProductDetails`, payload);
        
        // Store the product ID if returned
        if (response.data && response.data.collabrixProductID) {
          const updatedStepData = {
            ...stepData,
            collabrixProductID: response.data.collabrixProductID,
          };
          setStepData(updatedStepData);
          updateStepData("productDetails", updatedStepData);
          sessionStorage.setItem("addedProductDetails", "true");
        }
      }

      // Mark API call as successful if function exists
      if (
        typeof markApiCallSuccessful === "function" &&
        response.data &&
        response.data.status
      ) {
        markApiCallSuccessful();
        return; // Exit early since markApiCallSuccessful handles navigation
      }

      // If no markApiCallSuccessful, proceed to next step
      if (typeof handleNext === "function") {
        handleNext();
      }
    } catch (error) {
      console.error("Error saving product details:", error);
      // Still proceed to next step even if API fails (for development)
      if (typeof handleNext === "function") {
        handleNext();
      }
    } finally {
      setIsLoading(prev => ({ ...prev, savingData: false }));
    }
  };

  // Update formData when it changes - map CMO fields from cmoProductDetailsList if direct fields are missing
  useEffect(() => {
    if (formData?.productDetails) {
      const productDetails = formData.productDetails;
      
      setStepData(prev => {
        // Map CMO fields from cmoProductDetailsList[0] if direct fields are missing (for edit mode)
        const mappedProductDetails = {
          ...productDetails,
          // If cmoProductCode is missing but cmoProductDetailsList has data, use first item
          cmoProductCode: productDetails.cmoProductCode || 
                          productDetails.cmoProductDetailsList?.[0]?.cmoProductCode || 
                          "",
          cmoProductDescription: productDetails.cmoProductDescription || 
                                productDetails.cmoProductDetailsList?.[0]?.cmoProductDescription || 
                                "",
          cmoUOM: productDetails.cmoUOM || 
                  productDetails.cmoProductDetailsList?.[0]?.cmoUOM || 
                  "",
          // Ensure cmoProductDetailsList is always an array
          cmoProductDetailsList: productDetails.cmoProductDetailsList || [],
          // Map qaCheckRequired from processFlowConfiguration (like commercial supply)
          // But preserve the current value if it exists (user might have changed it)
          qaCheckRequired: prev?.qaCheckRequired !== undefined && prev?.qaCheckRequired !== null
            ? prev.qaCheckRequired
            : (formData?.processFlowConfiguration?.sponsorCQAApprovalReq ?? 
               formData?.processFlowConfiguration?.qaCheckRequired ?? 
               productDetails?.qaCheckRequired ?? 
               false),
        };
        
        return { ...prev, ...mappedProductDetails };
      });
    }
  }, [formData?.productDetails, formData?.processFlowConfiguration]);

  const shouldShowEditableField = (fieldName, readOnly, selectedProduct) => {
    // If readOnly is true, no fields should be editable
    if (readOnly) return false;

    // If we have a selectedProduct, only allow editing of specific fields
    // Matching Commercial Supply - these fields are NOT editable in edit mode:
    // productCode, productDescription, productModality, productTypeName, manufacturingFacilityType, manufactureSupplyType, cmoPlantId (CMO Name)
    if (selectedProduct && Object.keys(selectedProduct).length > 0) {
      const editableFields = [
        // "cmoPlantId", // CMO Name is NOT editable - matching Commercial Supply
        "cmoProductCode",
        "cmoProductDescription",
        "brand",
        "status",
        "receivingStorageLocationIDsFromSponsorERP",
        "receivingPlantIDFromSponsorERP",
        "cmoUOM",
        "sponsorUOM",
        "qaCheckRequired", // Clinical Supply uses qaCheckRequired, Commercial uses sponsorCQAApprovalReq (but they're the same field)
      ];
      return editableFields.includes(fieldName);
    }

    // If no selectedProduct, all fields are editable
    return true;
  };

  // Brand dialog handlers
  const handleOpenBrandDialog = (event) => {
    event.preventDefault();
    setOpenBrandDialog(true);
  };

  const handleCloseBrandDialog = () => {
    setOpenBrandDialog(false);
    setNewBrand("");
    setBrandError("");
  };

  const handleAddNewBrand = () => {
    // Validate that brand name is not empty
    if (!newBrand.trim()) {
      setBrandError("Brand name cannot be empty");
      return;
    }

    // Validate brand name format (Alphabets, digits, space, Hyphens (-), Ampersand (&), Underscore (_), Apostrophe ('), Period (.))
    const brandNameRegex = /^[A-Za-z0-9 \-&_'\.]+$/;
    if (!brandNameRegex.test(newBrand.trim())) {
      setBrandError(
        "Brand name can only contain alphabets, digits, spaces, hyphens (-), ampersands (&), underscores (_), apostrophes ('), and periods (.)"
      );
      return;
    }

    // Check if this brand already exists in the list to avoid duplicates
    let brandExists = false;
    if (Array.isArray(brands)) {
      for (const brand of brands) {
        if (typeof brand === "object" && brand !== null && brand.brandName) {
          if (brand.brandName.toLowerCase() === newBrand.trim().toLowerCase()) {
            brandExists = true;
            break;
          }
        } else if (typeof brand === "string") {
          if (brand.toLowerCase() === newBrand.trim().toLowerCase()) {
            brandExists = true;
            break;
          }
        }
      }
    }

    if (brandExists) {
      setBrandError("This brand already exists in the list");
      return;
    }

    try {
      const newBrandName = newBrand.trim();
      
      // Add new brand to the list (as object with brandID and brandName)
      const newBrandObj = {
        brandID: Date.now(), // Temporary ID
        brandName: newBrandName
      };
      const updatedBrands = Array.isArray(brands) ? [...brands, newBrandObj] : [newBrandObj];
      setBrands(updatedBrands);

      // Set the new brand as the selected value
      handleChange({
        target: {
          name: "brand",
          value: newBrandName,
        },
      });
      
      // Close the dialog
      handleCloseBrandDialog();
    } catch (error) {
      setBrandError("Failed to add new brand. Please try again.");
    }
  };

  return (
    <div className={parentClasses?.formContainer || ""}>
      <form>
        <Typography variant="h6" className={parentClasses?.formTitle}>
          Product Details
        </Typography>
        <Grid className="two-col-forms-grid" container spacing={3}>
          {/* Left Column */}
          
          {/* Product Code */}
          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="productCode">
                Product Code {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField("productCode", readOnly, selectedProduct) ? (
                <TextField
                  id="productCode"
                  value={stepData?.productCode || ""}
                  onChange={handleChange}
                  name="productCode"
                  variant="outlined"
                  placeholder="Enter text here"
                  fullWidth
                  disabled={readOnly}
                  InputProps={{ readOnly: readOnly, style: commonInlineStyles.input }}
                  sx={inputFieldStyles}
                  inputProps={commonInlineStyles.inputProps}
                  style={commonInlineStyles.input}
                  error={Boolean(errors.productCode)}
                  helperText={errors.productCode}
                />
              ) : (
                <Typography>{readOnly ? formData?.productDetails?.productCode || "N/A" : stepData?.productCode || "N/A"}</Typography>
              )}
            </FormControl>
          </Grid>

          {/* Product Description - Right Column */}
          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="productDescription">
                Product Description {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField("productDescription", readOnly, selectedProduct) ? (
                <TextField
                  id="productDescription"
                  value={stepData?.productDescription || ""}
                  onChange={handleChange}
                  name="productDescription"
                  variant="outlined"
                  placeholder="Enter text here"
                  fullWidth
                  disabled={readOnly}
                  InputProps={{ readOnly: readOnly, style: commonInlineStyles.input }}
                  sx={inputFieldStyles}
                  inputProps={commonInlineStyles.inputProps}
                  style={commonInlineStyles.input}
                  error={Boolean(errors.productDescription)}
                  helperText={errors.productDescription}
                />
              ) : (
                <Typography>{readOnly ? formData?.productDetails?.productDescription || "N/A" : stepData?.productDescription || "N/A"}</Typography>
              )}
            </FormControl>
          </Grid>

          {/* Product Modality - Left Column */}
          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="productModality">
                Product Modality {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField("productModality", readOnly, selectedProduct) ? (
                <>
                  <Select
                    id="productModality"
                    value={stepData?.productModality || ""}
                    onChange={handleChange}
                    name="productModality"
                    className={classes.select}
                    variant="outlined"
                    displayEmpty
                    fullWidth
                    disabled={readOnly || isLoading.productModalities}
                    style={commonInlineStyles.select}
                    inputProps={commonInlineStyles.inputProps}
                    error={Boolean(errors.productModality)}
                    renderValue={(selected) => !selected ? (isLoading.productModalities ? "Loading..." : "Select from the list") : selected}
                  >
                    {isLoading.productModalities ? (
                      <MenuItem value="" disabled>Loading...</MenuItem>
                    ) : productModalities && productModalities.length > 0 ? (
                      productModalities.map((modality) => (
                        <MenuItem key={modality.drugTypeID} value={modality.drugTypeName}>
                          {modality.drugTypeName}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="no_data" disabled>No modalities available</MenuItem>
                    )}
                  </Select>
                  {errors.productModality && <FormHelperText error>{errors.productModality}</FormHelperText>}
                </>
              ) : (
                <Typography>{formData?.productDetails?.drugTypeName || stepData?.productModality || "N/A"}</Typography>
              )}
            </FormControl>
          </Grid>

          {/* Product Type - Right Column */}
          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="productTypeName">
                Product Type {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField("productTypeName", readOnly, selectedProduct) ? (
                <>
                  <Select
                    id="productTypeName"
                    value={stepData?.productTypeName || ""}
                    onChange={handleChange}
                    name="productTypeName"
                    className={classes.select}
                    variant="outlined"
                    displayEmpty
                    fullWidth
                    disabled={readOnly || isLoading.productTypes}
                    style={commonInlineStyles.select}
                    inputProps={commonInlineStyles.inputProps}
                    error={Boolean(errors.productTypeName)}
                    renderValue={(selected) => !selected ? (isLoading.productTypes ? "Loading..." : "Select from the list") : selected}
                  >
                    {isLoading.productTypes ? (
                      <MenuItem value="" disabled>Loading...</MenuItem>
                    ) : productTypes && productTypes.length > 0 ? (
                      productTypes.map((type) => (
                        <MenuItem key={type.productTypeID} value={type.productTypeName}>
                          {type.productTypeName}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="no_data" disabled>No product types available</MenuItem>
                    )}
                  </Select>
                  {errors.productTypeName && <FormHelperText error>{errors.productTypeName}</FormHelperText>}
                </>
              ) : (
                <Typography>{formData?.productDetails?.productTypeName || stepData?.productTypeName || "N/A"}</Typography>
              )}
            </FormControl>
          </Grid>

          {/* Manufacturing Facility Type - Left Column */}
          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="manufacturingFacilityType">
                Manufacturing Facility Type {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField("manufacturingFacilityType", readOnly, selectedProduct) ? (
                <>
                  <Select
                    id="manufacturingFacilityType"
                    value={stepData?.manufacturingFacilityType || ""}
                    onChange={handleChange}
                    name="manufacturingFacilityType"
                    className={classes.select}
                    variant="outlined"
                    displayEmpty
                    fullWidth
                    disabled={readOnly || isLoading.facilityTypes}
                    style={commonInlineStyles.select}
                    inputProps={commonInlineStyles.inputProps}
                    error={Boolean(errors.manufacturingFacilityType)}
                    renderValue={(selected) => !selected ? (isLoading.facilityTypes ? "Loading..." : "Select from the list") : selected}
                  >
                    {isLoading.facilityTypes ? (
                      <MenuItem value="" disabled>Loading...</MenuItem>
                    ) : facilityTypes && facilityTypes.length > 0 ? (
                      facilityTypes.map((type) => (
                        <MenuItem key={type.facilityTypeID} value={type.facilityTypeName}>
                          {type.facilityTypeName}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="no_data" disabled>No facility types available</MenuItem>
                    )}
                  </Select>
                  {errors.manufacturingFacilityType && <FormHelperText error>{errors.manufacturingFacilityType}</FormHelperText>}
                </>
              ) : (
                <Typography>{formData?.productDetails?.facilityTypeName || stepData?.manufacturingFacilityType || "N/A"}</Typography>
              )}
            </FormControl>
          </Grid>

          {/* Manufacturing Supply Type - Right Column */}
          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="manufactureSupplyType">
                Manufacturing Supply Type
              </Typography>
              <Typography>
                {formData?.productDetails?.supplyTypeName || selectedProductType?.supplyTypeName || "N/A"}
              </Typography>
            </FormControl>
          </Grid>

          {/* CMO Name* - Left Column */}
          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="cmoPlantId">
                CMO Name {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField("cmoPlantId", readOnly, selectedProduct) ? (
                <>
                  <Select
                    id="cmoPlantId"
                    name="cmoPlantId"
                    value={stepData?.vendorMosymphonyID && stepData?.vendorName
                      ? `${stepData?.vendorMosymphonyID}|${stepData?.vendorName}|${stepData?.vendorPlant || ""}`
                      : ""}
                    onChange={(e) => {
                      const [vendorMosymphonyID, vendorName, vendorPlant] = e.target.value.split("|");
                      setStepData((prev) => ({
                        ...prev,
                        vendorMosymphonyID: Number(vendorMosymphonyID),
                        vendorName,
                        vendorPlant: vendorPlant || "",
                        cmoPlantId: e.target.value,
                      }));
                      updateStepData("productDetails", {
                        ...stepData,
                        vendorMosymphonyID: Number(vendorMosymphonyID),
                        vendorName,
                        vendorPlant: vendorPlant || "",
                        cmoPlantId: e.target.value,
                      });
                      if (errors.cmoPlantId) {
                        setErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.cmoPlantId;
                          return newErrors;
                        });
                      }
                    }}
                    variant="outlined"
                    displayEmpty
                    style={commonInlineStyles.select}
                    inputProps={commonInlineStyles.inputProps}
                    error={Boolean(errors.cmoPlantId)}
                    disabled={readOnly || isLoading.vendorPlants || vendorPlants.length === 0}
                    renderValue={(selected) => {
                      if (!selected) return "Select from the list";
                      const [vendorMosymphonyID, vendorName] = selected.split("|");
                      return `${vendorName}`;
                    }}
                  >
                    {isLoading.vendorPlants ? (
                      <MenuItem value="" disabled>Loading vendor plants...</MenuItem>
                    ) : vendorPlants.length > 0 ? (
                      vendorPlants
                        .filter((plant) => plant?.vendorMosymphonyID)
                        .map((plant) => (
                          <MenuItem
                            key={`${plant.vendorMosymphonyID}|${plant.vendorName}|${plant.vendorPlant || ""}`}
                            value={`${plant.vendorMosymphonyID}|${plant.vendorName}|${plant.vendorPlant || ""}`}
                          >
                            {`${plant.vendorName}`}
                          </MenuItem>
                        ))
                    ) : (
                      <MenuItem value="" disabled>No vendor plants available</MenuItem>
                    )}
                  </Select>
                  {errors.cmoPlantId && <FormHelperText error>{errors.cmoPlantId}</FormHelperText>}
                </>
              ) : (
                <Typography>{readOnly ? `${formData?.productDetails?.vendorName}` : `${stepData?.vendorName}`}</Typography>
              )}
            </FormControl>
          </Grid>

          {/* CMO Product Code* - Right Column */}
          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="cmoProductCode">
                CMO Product Code {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField("cmoProductCode", readOnly, selectedProduct) ? (
                <TextField
                  id="cmoProductCode"
                  name="cmoProductCode"
                  value={stepData?.cmoProductCode || ""}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="Enter text here"
                  fullWidth
                  style={commonInlineStyles.input}
                  inputProps={commonInlineStyles.inputProps}
                  error={Boolean(errors.cmoProductCode || errors["cmoProductCode-0"])}
                  helperText={errors.cmoProductCode || errors["cmoProductCode-0"]}
                />
              ) : (
                <Typography>
                  {readOnly 
                    ? (formData?.productDetails?.cmoProductDetailsList?.[0]?.cmoProductCode || 
                       formData?.productDetails?.cmoProductCode || 
                       "N/A")
                    : (stepData.cmoProductCode || "N/A")
                  }
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* CMO Product Description* - Left Column */}
          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="cmoProductDescription">
                CMO Product Description {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField("cmoProductDescription", readOnly, selectedProduct) ? (
                <TextField
                  id="cmoProductDescription"
                  name="cmoProductDescription"
                  value={stepData?.cmoProductDescription || ""}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="Enter text here"
                  fullWidth
                  style={commonInlineStyles.input}
                  inputProps={commonInlineStyles.inputProps}
                  error={Boolean(errors.cmoProductDescription || errors["cmoProductDescription-0"])}
                  helperText={errors.cmoProductDescription || errors["cmoProductDescription-0"]}
                />
              ) : (
                <Typography>
                  {readOnly 
                    ? (formData?.productDetails?.cmoProductDetailsList?.[0]?.cmoProductDescription || 
                       formData?.productDetails?.cmoProductDescription || 
                       "N/A")
                    : (stepData.cmoProductDescription || "N/A")
                  }
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Brand* - Right Column */}
          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="brand">
                Brand {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField("brand", readOnly, selectedProduct) ? (
                <>
                  <Select
                    id="brand"
                    value={stepData?.brand || ""}
                    onChange={handleChange}
                    name="brand"
                    className={classes.select}
                    variant="outlined"
                    displayEmpty
                    fullWidth
                    disabled={readOnly || isLoading.brands}
                    style={commonInlineStyles.select}
                    inputProps={commonInlineStyles.inputProps}
                    error={Boolean(errors.brand)}
                    renderValue={(selected) => !selected ? (isLoading.brands ? "Loading..." : "Select from the list") : selected}
                  >
                    {isLoading.brands ? (
                      <MenuItem value="" disabled>Loading...</MenuItem>
                    ) : brands && brands.length > 0 ? (
                      brands.map((brand, index) => {
                        // Handle both object and string formats - matching Commercial Supply
                        const brandValue =
                          typeof brand === "object" && brand !== null
                            ? brand.brandName || brand.name || brand.id || ""
                            : brand;
                        const brandDisplay =
                          typeof brand === "object" && brand !== null
                            ? brand.brandName || brand.name || brand.id || ""
                            : brand;
                        const brandKey =
                          typeof brand === "object" && brand !== null
                            ? brand.brandID || brand.id || index
                            : index;

                        return (
                          <MenuItem key={brandKey} value={brandValue}>
                            {brandDisplay}
                          </MenuItem>
                        );
                      })
                    ) : (
                      <MenuItem value="no_data" disabled>No brands available</MenuItem>
                    )}
                  </Select>
                  {errors.brand && <FormHelperText error>{errors.brand}</FormHelperText>}
                  <Box mt={1} className="line-height-initial">
                    <Typography
                      style={{ display: "inline-block" }}
                      className="fw-500 f-13px line-height-initial"
                    >
                      Brand not in the list?
                    </Typography>
                    <Button
                      onClick={handleOpenBrandDialog}
                      color="primary"
                      variant="text"
                      size="small"
                      className="fw-400 add-new-brand-btn line-height-initial"
                    >
                      Click here to add new brand
                    </Button>
                  </Box>
                </>
              ) : (
                <Typography>{readOnly ? formData?.productDetails?.brand || "N/A" : stepData.brand || "N/A"}</Typography>
              )}
            </FormControl>
          </Grid>

          {/* Sponsor UOM* - Left Column */}
          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="sponsorUOM">
                Sponsor UOM {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField("sponsorUOM", readOnly, selectedProduct) ? (
                <>
                  <TextField
                    id="sponsorUOM"
                    name="sponsorUOM"
                    value={stepData?.sponsorUOM || ""}
                    onChange={handleChange}
                    variant="outlined"
                    placeholder="Enter text here"
                    fullWidth
                    style={commonInlineStyles.input}
                    inputProps={commonInlineStyles.inputProps}
                    error={Boolean(errors?.sponsorUOM)}
                  />
                  {errors.sponsorUOM && <FormHelperText error>{errors.sponsorUOM}</FormHelperText>}
                </>
              ) : (
                <Typography>{readOnly ? formData?.productDetails?.sponsorUOM || "N/A" : stepData?.sponsorUOM || "N/A"}</Typography>
              )}
            </FormControl>
          </Grid>

          {/* CMO UOM* - Right Column */}
          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="cmoUOM">
                CMO UOM {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField("cmoUOM", readOnly, selectedProduct) ? (
                <>
                  <TextField
                    id="cmoUOM"
                    name="cmoUOM"
                    value={stepData?.cmoUOM || ""}
                    onChange={handleChange}
                    variant="outlined"
                    placeholder="Enter text here"
                    fullWidth
                    style={commonInlineStyles.input}
                    inputProps={commonInlineStyles.inputProps}
                    error={Boolean(errors?.cmoUOM || errors["cmoUOM-0"])}
                  />
                  {(errors.cmoUOM || errors["cmoUOM-0"]) && <FormHelperText error>{errors.cmoUOM || errors["cmoUOM-0"]}</FormHelperText>}
                </>
              ) : (
                <Typography>
                  {readOnly 
                    ? (formData?.productDetails?.cmoProductDetailsList?.[0]?.cmoUOM || 
                       formData?.productDetails?.cmoUOM || 
                       "N/A")
                    : (stepData?.cmoUOM || "N/A")
                  }
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Status* - Left Column */}
          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="status">
                Status {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField("status", readOnly, selectedProduct) ? (
                <>
                  <Select
                    id="status"
                    value={stepData?.status || ""}
                    onChange={handleChange}
                    name="status"
                    className={classes.select}
                    variant="outlined"
                    displayEmpty
                    fullWidth
                    disabled={readOnly}
                    style={commonInlineStyles.select}
                    inputProps={commonInlineStyles.inputProps}
                    error={Boolean(errors.status)}
                    renderValue={(selected) => !selected ? "Select from the list" : selected}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                  {errors.status && <FormHelperText error>{errors.status}</FormHelperText>}
                </>
              ) : (
                <Typography>{readOnly ? formData?.productDetails?.status || "N/A" : stepData.status || "N/A"}</Typography>
              )}
            </FormControl>
          </Grid>

          {/* Receiving Plant ID From Sponsor ERP - Right Column */}
          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="receivingPlantIDFromSponsorERP">
                Receiving Plant ID From Sponsor ERP
              </Typography>
              {shouldShowEditableField("receivingPlantIDFromSponsorERP", readOnly, selectedProduct) ? (
                <>
                  <Select
                    id="receivingPlantIDFromSponsorERP"
                    name="receivingPlantIDFromSponsorERP"
                    value={stepData?.receivingPlantIDFromSponsorERP || ""}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    displayEmpty
                    className={classes.select}
                    style={commonInlineStyles.select}
                    inputProps={commonInlineStyles.inputProps}
                    error={Boolean(errors.receivingPlantIDFromSponsorERP)}
                    disabled={!(stepData?.vendorMosymphonyID) || receivingPlantIDs.length === 0}
                    renderValue={(selected) =>
                      !selected
                        ? !(stepData?.vendorMosymphonyID)
                          ? "Please select CMO - Plant ID first"
                          : "Select from the list"
                        : selected
                    }
                  >
                    {receivingPlantIDs.map((plantID, index) => (
                      <MenuItem key={`plantID-${index}-${plantID}`} value={plantID}>
                        {plantID}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.receivingPlantIDFromSponsorERP && (
                    <FormHelperText error>{errors.receivingPlantIDFromSponsorERP}</FormHelperText>
                  )}
                </>
              ) : (
                <Typography>
                  {formData?.productDetails?.receivingPlantIDFromSponsorERP || stepData?.receivingPlantIDFromSponsorERP || "N/A"}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Receiving Storage Location IDs From Sponsor ERP - Left Column (Multi-select) */}
          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="receivingStorageLocationIDsFromSponsorERP">
                Receiving Storage Location IDs From Sponsor ERP
              </Typography>
              {shouldShowEditableField("receivingStorageLocationIDsFromSponsorERP", readOnly, selectedProduct) ? (
                <>
                  <Select
                    id="receivingStorageLocationIDsFromSponsorERP"
                    multiple
                    name="receivingStorageLocationIDsFromSponsorERP"
                    value={(() => {
                      const storedValue = stepData?.receivingStorageLocationIDsFromSponsorERP;
                      if (typeof storedValue === "string" && storedValue) {
                        return storedValue.split(",").map((item) => item.trim()).filter((item) => item !== "");
                      }
                      return Array.isArray(storedValue) ? storedValue : [];
                    })()}
                    onChange={handleChange}
                    variant="outlined"
                    renderValue={(selected) => {
                      if (!(stepData?.vendorMosymphonyID)) {
                        return "Please select CMO - Plant ID first";
                      }
                      if (typeof selected === "string") {
                        return selected || "";
                      }
                      return Array.isArray(selected) && selected.length > 0 ? selected.join(", ") : "";
                    }}
                    className={classes.select}
                    style={commonInlineStyles.select}
                    inputProps={commonInlineStyles.inputProps}
                    displayEmpty
                    disabled={!(stepData?.vendorMosymphonyID) || receivingStorageLocationIDs.length === 0}
                  >
                    {receivingStorageLocationIDs.map((locationID, index) => (
                      <MenuItem key={`locationID-${index}-${locationID}`} value={locationID}>
                        <Checkbox
                          checked={(() => {
                            const storedValue = stepData?.receivingStorageLocationIDsFromSponsorERP;
                            if (typeof storedValue === "string" && storedValue) {
                              const valuesArray = storedValue.split(",").map((item) => item.trim());
                              return valuesArray.includes(locationID);
                            }
                            return Array.isArray(storedValue) && storedValue.includes(locationID);
                          })()}
                        />
                        <ListItemText primary={locationID} />
                      </MenuItem>
                    ))}
                  </Select>
                </>
              ) : (
                <Typography>
                  {(() => {
                    const value = formData?.productDetails?.receivingStorageLocationIDsFromSponsorERP || stepData?.receivingStorageLocationIDsFromSponsorERP;
                    if (typeof value === "string" && value) {
                      return value;
                    }
                    if (Array.isArray(value) && value.length > 0) {
                      return value.join(", ");
                    }
                    return "N/A";
                  })()}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* QA Check Required - Right Column */}
          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="qaCheckRequired">
                QA Check Required
              </Typography>
              {shouldShowEditableField("qaCheckRequired", readOnly, selectedProduct) ? (
                <>
                  <Select
                    id="qaCheckRequired"
                    name="qaCheckRequired"
                    value={stepData?.qaCheckRequired !== undefined && stepData?.qaCheckRequired !== null
                      ? stepData?.qaCheckRequired
                      : (formData?.processFlowConfiguration?.sponsorCQAApprovalReq !== undefined && formData?.processFlowConfiguration?.sponsorCQAApprovalReq !== null
                          ? formData?.processFlowConfiguration?.sponsorCQAApprovalReq
                          : (formData?.processFlowConfiguration?.qaCheckRequired !== undefined && formData?.processFlowConfiguration?.qaCheckRequired !== null
                              ? formData?.processFlowConfiguration?.qaCheckRequired
                              : (formData?.productDetails?.qaCheckRequired !== undefined && formData?.productDetails?.qaCheckRequired !== null
                                  ? formData?.productDetails?.qaCheckRequired
                                  : "")))}
                    onChange={handleChange}
                    variant="outlined"
                    displayEmpty
                    className={classes.select}
                    style={commonInlineStyles.select}
                    inputProps={commonInlineStyles.inputProps}
                    error={Boolean(errors.qaCheckRequired)}
                    renderValue={(selected) => {
                      if (selected === "" || selected === undefined || selected === null) {
                        return "Select from the list";
                      }
                      return selected === true ? "YES" : "NO";
                    }}
                  >
                    <MenuItem value={true}>YES</MenuItem>
                    <MenuItem value={false}>NO</MenuItem>
                  </Select>
                  {errors.qaCheckRequired && <FormHelperText error>{errors.qaCheckRequired}</FormHelperText>}
                </>
              ) : (
                <Typography>
                  {stepData?.qaCheckRequired === true || 
                   formData?.processFlowConfiguration?.sponsorCQAApprovalReq === true || 
                   formData?.processFlowConfiguration?.qaCheckRequired === true || 
                   formData?.productDetails?.qaCheckRequired === true ? "YES" : "NO"}
                </Typography>
              )}
            </FormControl>
          </Grid>
        </Grid>

        {/* Navigation buttons */}
        {typeof handleNext === "function" && typeof handleBack === "function" && typeof activeStep === "number" && typeof stepsLength === "number" && (
          <Box
            sx={{
              display: "flex",
              justifyContent: activeStep === 0 ? "center" : "space-between",
            }}
            className="stepper-navigation-btns"
            mt={4}
          >
            {activeStep !== 0 && (
              <>
                <Button
                  variant="outlined"
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  className={parentClasses?.button}
                >
                  Back
                </Button>
                <Box sx={{ flex: "1 1 auto" }} />
              </>
            )}
            {!readOnly && (
              <Button
                variant="contained"
                color="primary"
                onClick={(e) => {
                  e.preventDefault();
                  if (activeStep === stepsLength - 1) {
                    handleSubmit();
                  } else {
                    handleNextWithValidation();
                  }
                }}
                disabled={isLoading.savingData}
                className={parentClasses?.button}
              >
                {isLoading.savingData ? (
                  <>
                    <CircularProgress
                      size={20}
                      color="inherit"
                      style={{ marginRight: 8 }}
                    />
                    Saving...
                  </>
                ) : activeStep === stepsLength - 1 ? (
                  "Save"
                ) : (
                  "Save & Next"
                )}
              </Button>
            )}
          </Box>
        )}
      </form>

      {/* Add New Brand Dialog */}
      <Dialog
        open={openBrandDialog}
        onClose={handleCloseBrandDialog}
        aria-labelledby="brand-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="brand-dialog-title">Add New Brand</DialogTitle>
        <DialogContent>
          <Typography component="label" htmlFor="newBrandInput">
            Brand {!readOnly && <span style={{ color: "red" }}>*</span>}
          </Typography>
          <TextField
            id="newBrandInput"
            value={newBrand}
            onChange={(e) => {
              const value = e.target.value;
              setNewBrand(value);

              // Validate brand name format as user types
              const brandNameRegex = /^[A-Za-z0-9 \-&_'\.]+$/;

              if (!value.trim()) {
                setBrandError("Brand name cannot be empty");
              } else if (!brandNameRegex.test(value)) {
                setBrandError(
                  "Brand name can only contain alphabets, digits, spaces, hyphens (-), ampersands (&), underscores (_), apostrophes ('), and periods (.)"
                );
              } else {
                setBrandError("");
              }
            }}
            variant="outlined"
            fullWidth
            autoFocus
            InputProps={{
              readOnly: readOnly,
              style: commonInlineStyles.input,
            }}
            sx={inputFieldStyles}
            inputProps={commonInlineStyles.inputProps}
            style={commonInlineStyles.input}
            error={Boolean(brandError)}
            helperText={brandError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBrandDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleAddNewBrand}
            color="primary"
            disabled={!newBrand.trim() || Boolean(brandError)}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ProductDetails;
